import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import ICU from 'i18next-icu';
import HTTPBackend, { HttpBackendOptions } from 'i18next-http-backend';

export const supportedLanguages = [
  'en',
  'en-ca',
  'en-gb',
  'de-de',
  'es-es',
  'fr-ca',
  'fr-fr',
  'ro-ro',
  'nl-be',
  'fr-lu',
  'de-lu',
  'de-li',
  'nl-nl',
  'de-ch',
  'fr-be',
  'sk-sk',
  'lv-lv',
  'tr-tr',
];

// Extract build hash from CRA's main bundle for cache-busting, or use timestamp in dev
const buildHash = (() => {
  const script = document.querySelector('script[src*="/static/js/main."]');
  const match = script?.getAttribute('src')?.match(/main\.([a-f0-9]+)\.js/);
  return match?.[1] ?? String(Date.now());
})();

i18next
  .use(HTTPBackend)
  .use(new ICU())
  .use(initReactI18next)
  .init<HttpBackendOptions>({
    fallbackLng: 'en',
    supportedLngs: supportedLanguages,
    load: 'currentOnly',
    lowerCaseLng: true,
    debug: process.env.NODE_ENV !== 'production',
    ns: ['translation'],
    defaultNS: 'translation',
    interpolation: {
      escapeValue: false,
    },
    react: {
      useSuspense: true,
    },
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      queryStringParams: { v: buildHash },
    },
  });

/** Keys already reported this page load, so a re-rendering component doesn't flood Sentry. */
const reportedMissingKeys = new Set<string>();

/**
 * errors.ts is imported lazily: it imports this module, and statically it would drag the redux
 * store and analytics into everything that loads i18n, including Jest suites.
 */
async function reportMissingKey(error: Error, key: string) {
  const { logInternalError } = await import('./series3/lib/errors');
  logInternalError(error, { fingerprint: ['missing-i18n-key', key], tags: { i18nKey: key } });
}

/**
 * Throw when a key doesn't exist (only after translations are loaded), so dev and tests catch it
 * before merge. Production reports instead: there a missing key is almost always version skew — a
 * tab still running an older bundle against locale files from a newer deploy that removed the key —
 * and throwing takes the whole page down through the root error boundary.
 */
i18next.on('loaded', () => {
  const baseT = (i18next.t as any).bind(i18next);
  i18next.t = ((key: any, ...args: any[]) => {
    const opts = args[0];
    const hasDefault = opts && typeof opts === 'object' && 'defaultValue' in opts;
    const ns = (opts && (opts.ns as string)) || (i18next.options.defaultNS as string) || 'translation';
    const lang = i18next.language;
    const bundleLoaded = Boolean(lang && i18next.hasResourceBundle(lang, ns));
    if (!hasDefault && bundleLoaded && !i18next.exists(key, opts)) {
      const error = new Error(`Missing i18n key: "${key}"`);
      if (process.env.NODE_ENV !== 'production') {
        throw error;
      }
      const keyName = String(key);
      if (!reportedMissingKeys.has(keyName)) {
        reportedMissingKeys.add(keyName);
        void reportMissingKey(error, keyName);
      }
    }
    return baseT(key, ...args);
  }) as any;
});

const DEBUG_I18N = false;
if (DEBUG_I18N) {
  const origT = i18next.t;
  const debugT = ((key: any, ..._args: any[]) => key) as unknown as typeof origT;
  Object.assign(debugT, origT);
  i18next.t = debugT;
}

export default i18next;
